// components/Header/Header.js
import React, { useContext, useRef, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CartContext } from '../../contexts/CartContext';
import './Header.css';
import api from '../../utils/apis';
import getImageUrl from '../../utils/getImageUrl';
import { ShieldCheck, CheckCircle, Zap } from 'lucide-react';

const isTrue = (val) => val === 1 || val === '1' || val === true;
const Header = ({ toggleSidebar, toggleMobileSearch, mobileSearchOpen, isMobileView, userData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn, logout } = useAuth();
  const { cart = [] } = useContext(CartContext);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Use the passed isMobileView prop, but also have state for responsive updates
  const actualIsMobile = isMobileView !== undefined ? isMobileView : isMobile;

  // Keep `isMobile` in sync with viewport size when `isMobileView` isn't provided
  useEffect(() => {
    const updateIsMobile = () => {
      try {
        setIsMobile(window.innerWidth < 768);
      } catch {
        // ignore (server-side render safety)
      }
    };
    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);
    return () => window.removeEventListener('resize', updateIsMobile);
  }, []);

  const hideSearchRoutes = ['/checkout', '/cart', '/products'];
  const shouldHideSearch = hideSearchRoutes.includes(location.pathname);

  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const desktopInputRef = useRef(null);
  const mobileSearchRef = useRef(null);
  const dropdownRef = useRef(null);
  const profileMenuRef = useRef(null);
  const skipSearchRef = useRef(false);

  const cartItemCount = cart.length;

  // --- SEARCH API ---
  useEffect(() => {
    const shouldSkipDropdown = skipSearchRef.current;
    if (shouldSkipDropdown) {
      skipSearchRef.current = false;
    }

    if (!searchTerm.trim()) {
      setResults([]);
      setShowDropdown(false);
      setLoadingSearch(false);
      setSearchError(null);
      return;
    }

    const controller = new AbortController();
    setLoadingSearch(true);
    setSearchError(null);

    const timer = setTimeout(async () => {
      try {
        const response = await api.get('/products', {
          params: { search: searchTerm.trim() },
          signal: controller.signal,
        });
        const data = response?.data?.data || response?.data;

        if (Array.isArray(data) && data.length === 0) {
          setResults([]);
          setSearchError('No products found');
        } else {
          setResults(Array.isArray(data) ? data : []);
          setSearchError(null);
        }

        if (!shouldSkipDropdown) {
          setShowDropdown(true);
        }
      } catch (err) {
        if (err?.name !== 'CanceledError' && err?.name !== 'AbortError') {
          console.error('Search request failed:', err);
          if (err?.response && err.response.status === 404) {
            setResults([]);
            setSearchError('No products found');
          } else {
            setResults([]);
            setSearchError('Search failed. Try again.');
          }
          if (!shouldSkipDropdown) {
            setShowDropdown(true);
          }
        }
      } finally {
        setLoadingSearch(false);
      }
    }, 300);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // --- CLICK OUTSIDE HANDLER ---
  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;

      // Close mobile search
      if (
        mobileSearchOpen &&
        mobileSearchRef.current &&
        !mobileSearchRef.current.contains(target)
      ) {
        const mobileSearchButton = document.getElementById('mobile-search-button');
        if (!mobileSearchButton || !mobileSearchButton.contains(target)) {
          toggleMobileSearch?.(false);
        }
      }

      // Close dropdown
      if (
        showDropdown &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        desktopInputRef.current &&
        !desktopInputRef.current.contains(target)
      ) {
        setShowDropdown(false);
      }

      // Close profile menu
      if (
        showProfileMenu &&
        profileMenuRef.current &&
        !profileMenuRef.current.contains(target)
      ) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [mobileSearchOpen, showDropdown, showProfileMenu, toggleMobileSearch]);

  // --- ESCAPE KEY ---
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (mobileSearchOpen) toggleMobileSearch?.(false);
        if (showDropdown) setShowDropdown(false);
        if (showProfileMenu) setShowProfileMenu(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mobileSearchOpen, showDropdown, showProfileMenu, toggleMobileSearch]);

  // --- NAVIGATION HANDLERS ---
  const goHome = () => {
    if (mobileSearchOpen) toggleMobileSearch?.(false);
    toggleSidebar?.(false);
    navigate('/');
  };

  const handleLoginClick = () => {
    if (isLoggedIn) logout();
    else navigate('/login');
  };



  const handleCartClick = (e) => {
    e?.stopPropagation?.();
    if (mobileSearchOpen) toggleMobileSearch?.(false);
    toggleSidebar?.(false);
    navigate('/cart');
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const term = searchTerm.trim();
    if (!term) return;

    setShowDropdown(false);
    if (mobileSearchOpen) toggleMobileSearch?.(false);
  };

  const handleResultClick = (product) => {
    const title = product?.title || product?.name || '';

    saveRecentSearch(title);

    skipSearchRef.current = true;
    setSearchTerm(title);
    setShowDropdown(false);

    const slag = product?.slag;

    setTimeout(() => {
      if (slag) navigate(`/product/${slag}`);
      else navigate('/products');

      if (mobileSearchOpen) toggleMobileSearch?.(false);
      toggleSidebar?.(false);
    }, 50);
  };

  // --- DROPDOWN CONTENT ---
  const renderDropdown = (isMobile = false) => (
    <div
      ref={dropdownRef}
      className={`absolute left-0 right-0 mt-1 bg-white border rounded-lg shadow-md overflow-auto ${isMobile ? 'max-h-60 z-60' : 'max-h-72'
        }`}
      role="listbox"
      aria-label="Search results"
    >
      {!searchTerm && recentSearches.length > 0 && (
        <div className="p-3">
          <div className="flex justify-between items-center mb-2">
            <p className="text-xs text-gray-400 font-medium">Recent Searches</p>
            <button type="button" onClick={handleClearRecent} className="text-xs text-primary font-medium hover:underline">Clear All</button>
          </div>
          {recentSearches.map((item, index) => (
            <div
              key={index}
              onClick={() => {
                setSearchTerm(item);
                desktopInputRef.current?.focus();
              }}
              className="flex items-center space-x-3 py-1.5 cursor-pointer hover:bg-gray-50 px-2 rounded-md"
            >
              <i className="fas fa-clock text-gray-400 text-xs"></i>
              <span className="text-sm text-gray-700">{item}</span>
            </div>
          ))}
        </div>
      )}

      {searchTerm && loadingSearch && <div className="p-3 text-sm">Searching...</div>}
      {searchTerm && searchError && !loadingSearch && (
        <div className="p-3 text-sm text-gray-500">{searchError}</div>
      )}
      {searchTerm && !loadingSearch && !searchError && results.length === 0 && (
        <div className="p-3 text-sm text-gray-500">No products found</div>
      )}
      {searchTerm && !loadingSearch &&
        results.map((p) => (
          <div
            key={p.id ?? p.product_id ?? JSON.stringify(p)}
            onClick={() => handleResultClick(p)}
            className="p-3 hover:bg-gray-50 cursor-pointer flex items-center space-x-3"
            role="option"
            aria-selected={false}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleResultClick(p);
            }}
          >
            {!isMobile && (
              <div className="w-10 h-10 bg-gray-100 rounded-md flex-shrink-0 overflow-hidden flex items-center justify-center">
                {getImageUrl(p.product_img || p.image_url) ? (
                  <img
                    src={getImageUrl(p.product_img || p.image_url)}
                    alt={p.name || p.title || 'product'}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : null}
                <i className="fas fa-box text-gray-400" style={{ display: getImageUrl(p.product_img || p.image_url) ? 'none' : 'block' }} />
              </div>
            )}
            <div className="flex-1 text-sm overflow-hidden">
              <div className="font-medium truncate max-w-[300px] lg:max-w-[500px] text-gray-800" title={p.title}>{p.title}</div>

              <div className="flex items-center gap-x-2 gap-y-1 mt-1 flex-wrap">
                {/* Badges */}
                {isTrue(p.is_trusted) && (
                  <span className="flex items-center gap-0.5 bg-[#F0FDF4] border border-[#BBF7D0] text-[#15803D] text-[9px] font-bold px-1.5 py-0.5 rounded-sm uppercase tracking-wide">
                    <ShieldCheck className="w-2.5 h-2.5" />
                    <span>Trusted</span>
                  </span>
                )}
                {isTrue(p.is_mexa_verified) && (
                  <span className="flex items-center gap-0.5 bg-[#EFF6FF] border border-[#BFDBFE] text-[#1E40AF] text-[9px] font-bold px-1.5 py-0.5 rounded-sm uppercase tracking-wide">
                    <CheckCircle className="w-2.5 h-2.5" />
                    <span>Verified</span>
                  </span>
                )}
                {(isTrue(p.is_premium) || isTrue(p?.store?.is_premium)) && (
                  <span className="flex items-center gap-0.5 bg-white border border-[#0F766E]/20 text-[#0F766E] shadow-sm text-[9px] font-bold px-1.5 py-0.5 rounded-sm uppercase tracking-wide">
                    <Zap className="w-2.5 h-2.5" />
                    <span>Premium</span>
                  </span>
                )}

                {/* Tags */}
                {p.tags && (
                  <div className="flex items-center gap-1 overflow-hidden">
                    {p.tags.split(',').slice(0, 3).map((tag, i) => (
                      <span key={i} className="text-[10px] text-gray-500 bg-gray-100/80 px-1.5 py-0.5 rounded-md border border-gray-200/60 truncate max-w-[80px]" title={tag.trim()}>
                        {tag.trim()}
                      </span>
                    ))}
                  </div>
                )}
              </div>

            </div>
          </div>
        ))}
    </div>
  );

  const RECENT_KEY = "recent_searches";

  const getRecentSearches = () => {
    try {
      return JSON.parse(localStorage.getItem(RECENT_KEY)) || [];
    } catch {
      return [];
    }
  };

  const saveRecentSearch = (term) => {
    if (!term) return;

    let updated = getRecentSearches().filter((item) => item !== term);
    updated.unshift(term);

    const finalList = updated.slice(0, 8);
    localStorage.setItem(RECENT_KEY, JSON.stringify(finalList));

    setRecentSearches(finalList);
  };

  const handleClearRecent = () => {
    localStorage.removeItem(RECENT_KEY);
    setRecentSearches([]);
  };
  useEffect(() => {
    if (mobileSearchOpen || showDropdown) {
      setRecentSearches(getRecentSearches());
    }
  }, [mobileSearchOpen, showDropdown]);

  return (
    <div className="fixed top-0 left-0 right-0 z-50 flex justify-center w-full pointer-events-none">
      <header className="w-full max-w-7xl mx-auto bg-white  pointer-events-auto h-auto pt-[env(safe-area-inset-top,0px)] transition-all">
        <div className="flex items-center justify-between px-4 h-16 md:h-20 w-full">
          {/* Left Section */}
          <div className="flex items-center md:w-64">
            <div
              className="flex items-center cursor-pointer"
              onClick={goHome}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter') goHome();
              }}
              aria-label="Go to homepage"
            >
              <h1 className="text-xl font-extrabold text-primary tracking-tight">
                <img
                  src={actualIsMobile ? '/images/mobile_logo.png' : '/images/logo.png'}
                  alt="QuickCart Logo"
                  className={actualIsMobile ? 'h-16 w-auto py-2' : 'w-full h-24 py-4'}
                />
              </h1>
            </div>
          </div>

          {/* Desktop Search */}
          {!shouldHideSearch && (
            <div className="hidden md:flex flex-1 max-w-2xl mx-8 relative">
              <form onSubmit={handleSearchSubmit} className="relative w-full group" autoComplete="off">
                <input
                  ref={desktopInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search for 'Paracetamol'..."
                  className="w-full py-2.5 pl-12 pr-12 rounded-full border border-[#0F766E]/15 bg-white text-sm focus:outline-none focus:border-[#0F766E] focus:ring-1 focus:ring-[#0F766E] shadow-[0_4px_12px_rgba(15,118,110,0.04)] transition-all"
                  onFocus={() => {
                    if (searchTerm.trim() || getRecentSearches().length > 0) {
                      setShowDropdown(true);
                    }
                  }}
                  aria-label="Search products"
                />
                <i className="fas fa-search absolute left-4 top-[14px] text-[#64748B]" />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="absolute right-4 top-[12px] text-gray-400 hover:text-slate-600 focus:outline-none"
                  >
                    <i className="fas fa-times-circle" />
                  </button>
                )}
                {showDropdown && renderDropdown()}
              </form>
            </div>
          )}

          {shouldHideSearch && <div className="hidden md:flex flex-1"></div>}

          {/* Right Section */}
          <div className="flex items-center space-x-3.5">
            {/* Search Icon Removed */}

            <button
              className="relative w-10 h-10 bg-white rounded-full border border-[#0F766E]/5 shadow-[0_4px_12px_rgba(15,118,110,0.08)] flex items-center justify-center text-[#0F766E] focus:outline-none active:scale-90 transition-all"
              aria-label="Cart"
              onClick={handleCartClick}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="9" cy="21" r="1" />
                <circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
              {cartItemCount > 0 && (
                <span className="absolute -top-1 -right-1 h-5 w-5 bg-secondary text-white text-[10px] font-extrabold flex items-center justify-center rounded-full border-2 border-white min-w-[20px]">
                  {cartItemCount > 99 ? '99+' : cartItemCount}
                </span>
              )}
            </button>

            <div id="header-auth-section" ref={profileMenuRef} className="relative flex items-center space-x-3">
              {!isLoggedIn && (
                <button
                  onClick={handleLoginClick}
                  className="bg-[#0F766E] text-white px-5 py-2 text-sm font-bold shadow-[0_4px_12px_rgba(15,118,110,0.12)] hover:bg-[#0D655E] active:scale-95 transition-all hidden md:block"
                >
                  Login
                </button>
              )}
              <button
                onClick={() => navigate('/menu')}
                className="relative w-10 h-10 bg-teal-50 rounded-full border border-teal-100 shadow-sm flex items-center justify-center text-[#0F766E] overflow-hidden focus:outline-none active:scale-95 transition-all"
              >
                {isLoggedIn && userData?.name ? (
                  <span className="font-bold text-lg">{userData.name.charAt(0).toUpperCase()}</span>
                ) : (
                  <i className="far fa-user text-lg" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Permanent Search Box Trigger Below Header (Mobile Only) */}
        {!shouldHideSearch && (
          <div className="px-4 pb-4 pt-1 w-full max-w-4xl mx-auto md:hidden">
            <div
              onClick={() => toggleMobileSearch?.(true)}
              className="relative w-full group cursor-text"
            >
              <div className="w-full py-2.5 pl-12 pr-12 rounded-full border border-[#0F766E]/15 bg-white text-sm text-gray-400 text-left focus:outline-none focus:border-[#0F766E] focus:ring-1 focus:ring-[#0F766E] shadow-inner transition-all flex items-center h-[42px]">
                Search for medicines, products...
              </div>
              <i className="fas fa-search absolute left-4 top-[14px] text-gray-400" />
            </div>
          </div>
        )}

        {/* Full-Screen Search Panel (Previous Behavior) */}
        {!shouldHideSearch && mobileSearchOpen && (
          <div
            ref={mobileSearchRef}
            className="fixed inset-0 bg-white z-[999] flex flex-col animate-fadeIn"
          >
            {/* HEADER */}
            <div className="flex items-center px-4 py-3 border-b bg-white sticky top-0 z-10">
              <button
                onClick={() => toggleMobileSearch(false)}
                className="mr-3 text-xl"
              >
                <i className="fas fa-arrow-left"></i>
              </button>

              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder="Search medicines, products..."
                  className="w-full py-2.5 pl-10 pr-3 rounded-lg border border-gray-300 bg-white focus:outline-none"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  autoFocus
                />
                <i className="fas fa-search absolute left-3 top-3 text-gray-400 text-sm"></i>
              </div>
            </div>

            {/* CONTENT */}
            <div className="flex-1 overflow-y-auto px-4 py-3">

              {/* 🔥 RECENT SEARCHES */}
              {!searchTerm && recentSearches.length > 0 && (
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <p className="text-xs text-gray-400 font-medium">
                      Recent Searches
                    </p>
                    <button
                      onClick={handleClearRecent}
                      className="text-sm text-primary font-medium"
                    >
                      Clear All
                    </button>
                  </div>

                  {recentSearches.map((item, index) => (
                    <div
                      key={index}
                      onClick={() => setSearchTerm(item)}
                      className="flex items-center space-x-3 py-2 cursor-pointer"
                    >
                      <i className="fas fa-clock text-gray-400 text-sm"></i>
                      <span className="text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* 🔍 SEARCH RESULTS */}
              {searchTerm && (
                <>
                  {loadingSearch && (
                    <div className="text-sm text-gray-500 py-2">Searching...</div>
                  )}

                  {searchError && (
                    <div className="text-sm text-gray-500 py-2">{searchError}</div>
                  )}

                  {!loadingSearch &&
                    results.map((p) => (
                      <div
                        key={p.id ?? p.product_id}
                        onClick={() => handleResultClick(p)}
                        className="flex items-center space-x-3 py-3 border-b cursor-pointer"
                      >
                        <div className="w-10 h-10 bg-gray-100 rounded-md flex items-center justify-center overflow-hidden flex-shrink-0">
                          {getImageUrl(p.product_img || p.image_url) ? (
                            <img
                              src={getImageUrl(p.product_img || p.image_url)}
                              alt="product"
                              className="w-full h-full object-cover"
                            />
                          ) : null}
                          <i className="fas fa-search text-gray-400 text-sm" style={{ display: getImageUrl(p.product_img || p.image_url) ? 'none' : 'block' }} />
                        </div>
                        <span className="text-sm line-clamp-2">{p.title}</span>
                      </div>
                    ))}
                </>
              )}
            </div>
          </div>
        )}
      </header>
    </div>
  );
};

export default Header;
